// Drives the access case's Billing Identity drawer
// (partners/provider_access_cases/_change_billing_identity_drawer).
//
// Two ways to name a new billing identity, and the form must submit exactly
// one signed candidate_token for whichever is active:
//
//   * an EIN search result card (partners/provider_access_cases/
//     _billing_identity_results, the Provider Intelligence EIN card in
//     `selection: :pick` mode). The card carries its signed token in
//     `data-token`; clicking it copies that token into the hidden
//     candidate_token input the search side owns. That input lives outside the
//     turbo-stream results container, so a later search can't remove it.
//   * the "Other" card, which carries its own hidden candidate_token holding
//     the server-minted manual-entry token plus the NPI/EIN inputs.
//
// Only one of the two inputs is ever enabled with a value, so the server sees a
// single candidate_token no matter how a form encoder orders duplicate names.
// The server re-verifies the token and re-validates the typed NPI/EIN; nothing
// here is a trust boundary.
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["searchToken", "resultCard", "otherCard", "otherCheckbox", "otherToken", "manualFields", "manualNpi", "manualEin", "submit"];
  static values = {
    // Mirrors _provider_result_card's `selected:` / unselected styling, toggled
    // on whichever card is picked.
    selectedClasses: { type: String, default: "border-core-purple-500 bg-[#FCFCFF] shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]" },
    unselectedClasses: { type: String, default: "border-support-light-300 hover:border-support-light-500 hover:shadow-[0_4px_16px_rgba(45,48,71,0.07)]" },
  };

  // A 422 re-render comes back with Other already selected and whatever was
  // typed still in its inputs, so derive the button state from the DOM rather
  // than assuming a fresh drawer.
  connect() {
    // Other owning the submission also means the search half must be inert, or
    // a re-rendered drawer would post an empty second candidate_token.
    if (this.otherSelected()) this.clearSearchSelection();
    this.updateSubmit();
  }

  // Clicking an EIN result card picks it: its signed token becomes the form's
  // candidate_token, Other is dropped, and the other result cards unmark.
  // Clicking the already-picked card deselects it again.
  chooseResult(event) {
    if (this.nestedControlClick(event)) return;
    this.cancelLabelActivation(event);
    const card = event.currentTarget;
    const token = card.dataset.token;
    if (!token || !this.hasSearchTokenTarget) return;

    if (this.isSelectedCard(card)) {
      this.clearSearchSelection();
      this.updateSubmit();
      return;
    }

    this.deselectOther();
    this.searchTokenTarget.value = token;
    this.searchTokenTarget.disabled = false;
    this.markResultCards(card);
    this.updateSubmit();
  }

  // Typing a new query drops the pick immediately, before the debounced search
  // even fires: the card it was made from is about to be replaced, so the
  // drawer must never still be holding a token for an organization that is no
  // longer on screen.
  searchInput() {
    this.clearSearchSelection();
    this.updateSubmit();
  }

  // A new search's turbo-stream response replaced the results region. If the
  // card that was picked is gone, so is the pick: otherwise an operator who
  // clicked an old card while the new search was in flight would still be
  // holding (and could submit) a token for an organization no longer on screen.
  resultCardTargetDisconnected(card) {
    if (!this.hasSearchTokenTarget || !this.searchTokenTarget.value) return;
    if (card.dataset.token !== this.searchTokenTarget.value) return;
    if (this.resultCardTargets.some((other) => other !== card && other.dataset.token === card.dataset.token && this.isSelectedCard(other))) return;

    this.clearSearchSelection();
    this.updateSubmit();
  }

  // The search box sits inside the form, so Enter in it would otherwise be an
  // implicit submission — committing whatever is currently picked when the
  // operator meant "search".
  ignoreEnter(event) {
    event.preventDefault();
  }

  // Clicking the Other card (anywhere but its checkbox) toggles it. Selecting it
  // takes the search's token away entirely (blanked AND disabled) so the typed
  // identity is the only one that can be submitted. Clicks on the checkbox or its
  // label are left to the checkbox's own change event (otherToggled), because the
  // browser has already flipped `checked` by the time the click bubbles here;
  // toggling again would undo a keyboard Space press.
  chooseOther(event) {
    if (event?.target?.closest?.("label, input[type='checkbox']")) return;

    if (this.otherSelected()) {
      this.deselectOther();
    } else {
      this.selectOther();
    }
    this.updateSubmit();
  }

  // The Other checkbox changed natively (mouse on the checkbox/label, or Space
  // from the keyboard): follow the state the browser already applied.
  otherToggled() {
    if (this.otherSelected()) {
      this.selectOther();
    } else {
      this.deselectOther();
    }
    this.updateSubmit();
  }

  // Typing in the NPI/EIN inputs only re-checks whether Other is submittable.
  manualInput() {
    this.updateSubmit();
  }

  // Clicks inside the Other card's inputs must not toggle the card itself.
  stopPropagation(event) {
    event.stopPropagation();
  }

  selectOther() {
    this.clearSearchSelection();
    this.markOther(true);
    if (this.hasOtherTokenTarget) this.otherTokenTarget.disabled = false;
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.classList.remove("hidden");
      this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = false; });
      this.manualFieldsTarget.querySelector("input")?.focus();
    }
  }

  deselectOther() {
    this.markOther(false);
    if (this.hasOtherTokenTarget) this.otherTokenTarget.disabled = true;
    if (this.hasManualFieldsTarget) {
      this.manualFieldsTarget.classList.add("hidden");
      this.manualFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = true; });
    }
  }

  // Drop a search selection whole: the signed token and the picked card's
  // highlight, so the drawer never shows an organization it won't send. The
  // typed query is left alone — it is what the visible results answer to.
  clearSearchSelection() {
    if (this.hasSearchTokenTarget) {
      this.searchTokenTarget.value = "";
      this.searchTokenTarget.disabled = true;
    }
    this.markResultCards(null);
  }

  // Single pick across the result cards: the active one gets the selected
  // styling and a checked box, every other one is unmarked. activeCard = null
  // clears them all.
  markResultCards(activeCard) {
    const on = this.selectedClassesValue.split(" ").filter(Boolean);
    const off = this.unselectedClassesValue.split(" ").filter(Boolean);
    this.resultCardTargets.forEach((card) => {
      const selected = card === activeCard;
      card.setAttribute("aria-selected", selected ? "true" : "false");
      card.classList.remove(...(selected ? off : on));
      card.classList.add(...(selected ? on : off));
      const box = card.querySelector("input[type='checkbox']");
      if (box) box.checked = selected;
    });
  }

  markOther(selected) {
    if (this.hasOtherCheckboxTarget) this.otherCheckboxTarget.checked = selected;
    if (!this.hasOtherCardTarget) return;

    const card = this.otherCardTarget;
    card.setAttribute("aria-selected", selected ? "true" : "false");
    const on = this.selectedClassesValue.split(" ").filter(Boolean);
    const off = this.unselectedClassesValue.split(" ").filter(Boolean);
    card.classList.remove(...(selected ? off : on));
    card.classList.add(...(selected ? on : off));
  }

  isSelectedCard(card) {
    return card.getAttribute("aria-selected") === "true";
  }

  // Each card's checkbox is a visually hidden <input> wrapped in a <label>
  // (shared/checkbox). Clicking the visible box hits the label, and the browser
  // then fires a second, synthetic click on the <input>. That click bubbles back
  // to the card, so the pick would toggle on and straight back off. Cancel the
  // label's activation and let markResultCards set the checkbox. A click whose
  // target is the input itself (keyboard Space) is left alone, so it still
  // toggles exactly once. Same fix as sca_google_places_controller.
  cancelLabelActivation(event) {
    const target = event?.target;
    if (!target?.closest || target.matches?.("input")) return;
    const label = target.closest(".gb-checkbox");
    if (label && event.currentTarget?.contains?.(label)) event.preventDefault();
  }

  // A button or link inside a card (the EIN card's "Show all N" associated-NPI
  // toggle) does its own thing and must not also pick or unpick the card.
  nestedControlClick(event) {
    const control = event?.target?.closest?.("button, a, select, textarea");
    return Boolean(control && event.currentTarget?.contains?.(control));
  }

  otherSelected() {
    return this.hasOtherCheckboxTarget && this.otherCheckboxTarget.checked;
  }

  searchTokenValue() {
    return this.hasSearchTokenTarget && !this.searchTokenTarget.disabled ? this.searchTokenTarget.value : "";
  }

  // Other needs a complete 10-digit NPI and 9-digit EIN (the server enforces
  // the same thing); a searched identity needs only its token.
  submittable() {
    if (this.otherSelected()) {
      const npi = this.hasManualNpiTarget ? this.digits(this.manualNpiTarget) : "";
      const ein = this.hasManualEinTarget ? this.digits(this.manualEinTarget) : "";
      return npi.length === 10 && ein.length === 9;
    }
    return Boolean(this.searchTokenValue());
  }

  digits(target) {
    return (target?.value ?? "").replace(/\D/g, "");
  }

  updateSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !this.submittable();
  }
}
